
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Check, Plus, Trash2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface Task {
  id: string;
  title: string;
  duration: number;
  completed: boolean;
  scheduledTime: string;
}

interface CommonTask {
  title: string;
  usageCount: number;
}

interface CommonTasksDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function CommonTasksDialog({ open, onOpenChange }: CommonTasksDialogProps) {
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDuration, setTaskDuration] = useState(30);
  const [commonTasks, setCommonTasks] = useState<CommonTask[]>(() => {
    const storedTasks = localStorage.getItem('commonTasks');
    return storedTasks ? JSON.parse(storedTasks) : [];
  });
  const { toast } = useToast();

  useEffect(() => {
    localStorage.setItem('commonTasks', JSON.stringify(commonTasks));
  }, [commonTasks]);

  const startTask = (title: string, duration: number) => {
    addToTasks(title, duration);
    onOpenChange(false);
  };

  const addToTasks = (title: string, duration: number) => {
    const newTask = {
      id: crypto.randomUUID(),
      title,
      duration,
      completed: false,
      scheduledTime: new Date().toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: true 
      }),
      createdAt: new Date().toISOString() // Add creation date
    };

    const existingTasks = JSON.parse(localStorage.getItem('tasks') || '[]');
    const updatedTasks = [...existingTasks, newTask];
    localStorage.setItem('tasks', JSON.stringify(updatedTasks));
    
    // Increment the task usage count
    incrementTaskUsage(title);
    
    toast({
      title: "Task Added",
      description: `${title} has been added to your tasks.`
    });
  };

  const incrementTaskUsage = (title: string) => {
    const existingTask = commonTasks.find(task => task.title === title);
    
    if (existingTask) {
      const updatedTasks = commonTasks.map(task =>
        task.title === title ? { ...task, usageCount: task.usageCount + 1 } : task
      );
      setCommonTasks(updatedTasks);
    } else {
      setCommonTasks([...commonTasks, { title, usageCount: 1 }]);
    }
  };

  const handleAddTask = () => {
    if (taskTitle.trim() !== "") {
      incrementTaskUsage(taskTitle);
      setTaskTitle("");
    }
  };

  const removeTask = (title: string) => {
    const updatedTasks = commonTasks.filter(task => task.title !== title);
    setCommonTasks(updatedTasks);
  };

  const clearTasks = () => {
    setCommonTasks([]);
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Common Tasks</DialogTitle>
          <DialogDescription>
            Add and manage tasks you do often.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="title" className="text-right">
              Task Title
            </Label>
            <Input
              id="title"
              value={taskTitle}
              onChange={(e) => setTaskTitle(e.target.value)}
              className="col-span-3"
            />
          </div>
          <Button onClick={handleAddTask} className="w-full justify-center">
            <Plus className="mr-2 h-4 w-4" /> Add Task
          </Button>
        </div>
        <Table>
          <TableCaption>A list of your common tasks.</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead>Task</TableHead>
              <TableHead>Usage Count</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {commonTasks.map((task) => (
              <TableRow key={task.title}>
                <TableCell className="font-medium">{task.title}</TableCell>
                <TableCell>{task.usageCount}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => startTask(task.title, taskDuration)}>
                    <Check className="mr-2 h-4 w-4" /> Start
                  </Button>
                  <Button variant="ghost" size="sm" className="text-destructive" onClick={() => removeTask(task.title)}>
                    <Trash2 className="mr-2 h-4 w-4" /> Remove
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={3} className="text-right">
                <Button variant="link" onClick={clearTasks}>Clear All</Button>
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" onClick={handleClose}>Close</Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
